import "./UpcomingMilestones.css";

import { FullRightArrowDark } from "../../../assets";
import { OrganisationTypeConstants } from "../../../constants";
import { UpcomingMilestonePropsWithKey } from "../../../models";
import { useAuth } from "../../../useAuth";
import { Button } from "../../forms";
import { TaskCard } from "../../TaskCard";
import { getMilestoneAssigneeInitials, getMilestoneDueDate, isMilestoneOverdue } from "./utils";

const DEFAULT_LOADING_CARD_COUNT = 3;

interface UpcomingMilestonesProps {
  milestones: UpcomingMilestonePropsWithKey[];
  onViewAll: () => void;
  /** Renders card skeletons instead of the empty message while the first load is in flight. */
  isLoading?: boolean;
  /**
   * Renders a failure message. Distinct from the empty state on purpose: a failed fetch used to
   * leave the list empty and so read as "you have no milestones", which is a different fact.
   */
  hasError?: boolean;
  /** How many skeletons to show while loading — the page's own milestone limit. */
  loadingCardCount?: number;
}

export const UpcomingMilestones = ({
  milestones,
  onViewAll,
  isLoading = false,
  hasError = false,
  loadingCardCount = DEFAULT_LOADING_CARD_COUNT,
}: UpcomingMilestonesProps): JSX.Element => {
  const { currentUserType } = useAuth();

  // Asset Manager keeps the rounded treatment; the other portals stay square.
  const isAssetManager = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;
  const cardVariant = isAssetManager ? "seed" : "hub";

  const hasMilestones = milestones.length > 0;
  const showCards = !isLoading && !hasError && hasMilestones;

  return (
    <div className={`UpcomingMilestones ${isAssetManager ? "Rounded" : ""}`}>
      <div className="UpcomingMilestonesHeader">
        <p className="body01">Upcoming milestones</p>
        {showCards && (
          <div className="AllUpcomingMilestones">
            <Button
              text="View more"
              onClick={onViewAll}
              variant="secondary"
              icon={<FullRightArrowDark />}
              minWidth={false}
              size="extraSmall"
              hasNoPadding
              fontWeight="light"
            />
          </div>
        )}
      </div>

      {isLoading && (
        <div className="UpcomingMilestonesList" aria-busy="true">
          {Array.from({ length: loadingCardCount }, (unused, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <TaskCard key={`milestone-skeleton-${index}`} mode="milestone-kpi" variant={cardVariant} state="loading" />
          ))}
        </div>
      )}

      {!isLoading && hasError && (
        <p className="body2 UpcomingMilestonesError" role="alert">
          Upcoming milestones could not be loaded. Please try again later.
        </p>
      )}

      {!isLoading && !hasError && !hasMilestones && <p className="body2 NoData">No upcoming milestones to display</p>}

      {showCards && (
        <div className="UpcomingMilestonesList">
          {milestones.map((milestone) => {
            const isOverdue = isMilestoneOverdue(milestone.deadlineDate, milestone.status);

            return (
              <TaskCard
                key={milestone.key}
                mode="milestone-kpi"
                variant={cardVariant}
                state={isOverdue ? "overdue" : "default"}
                title={milestone.name}
                linkedObjectName={milestone.project}
                assigneeName={milestone.assigneeName}
                assigneeInitials={getMilestoneAssigneeInitials(milestone.assigneeName)}
                dueDate={getMilestoneDueDate(milestone.deadlineDate)}
                onClick={milestone.onClick}
                onObjectClick={milestone.onProjectClick && (() => milestone.onProjectClick?.())}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
